import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Input,
  Select,
  Modal,
  Form,
  InputNumber,
  DatePicker,
  message,
  Space,
  Tooltip,
  Drawer,
  Descriptions,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import {
  shipmentApi,
  preDeclarationApi,
  routeApi,
  customerApi,
  cartonApi,
  type ShipmentDTO,
  type PreDeclarationDTO,
  type RouteDTO,
  type CustomerDTO,
  type CartonDTO,
  type SubmitDepotContextDTO,
  type SubmitToDepotRequest,
  type ShipmentInvoiceDTO,
  type TrackingDisplayDTO,
} from '../api/kfc';
import CartonDetailDrawer from '../components/CartonDetailDrawer';
import TrackingDisplayPanel from '../components/TrackingDisplayPanel';

const statusOptions = [
  { label: '预申报', value: 'PRE_DECLARATION' },
  { label: '已交代理仓', value: 'SUBMITTED_TO_DEPOT' },
  { label: '交货确认中', value: 'HANDOVER_CONFIRMING' },
  { label: '准备运输', value: 'CONFIRMED_WAIT_DEPARTURE' },
  { label: '运输中', value: 'IN_TRANSIT' },
  { label: '待派送', value: 'OUT_FOR_DELIVERY' },
  { label: '已签收', value: 'DELIVERED' },
];

const statusLabel: Record<string, string> = {
  PRE_DECLARATION: '预申报',
  PENDING_HANDOVER: '预申报',
  SUBMITTED_TO_DEPOT: '已交代理仓',
  HANDOVER_CONFIRMING: '交货确认中',
  CONFIRMED_WAIT_DEPARTURE: '准备运输',
  IN_TRANSIT: '运输中',
  OUT_FOR_DELIVERY: '待派送',
  DELIVERED: '已签收',
};

const cartonStateLabel: Record<string, string> = {
  preparing: '待收货',
  inKFYWarehouse: '抵达凯飞越仓库',
  inAgentWarehouse: '抵达代理仓库(出发地)',
  readyForShipping: '准备运输',
  onTheWay: '在途运输',
  arrivedDstPort: '抵达目的港',
  recevied: '已签收',
};

const agentInboundStatusLabel: Record<number, string> = {
  0: '新增',
  1: '已下单',
  2: '收货中',
  3: '已收货',
  4: '待确认',
  5: '已确认',
  6: '已删除',
  7: '调拨中',
  8: '扣货',
  9: '退货',
};
const agentOutboundStatusLabel: Record<number, string> = {
  0: '待出库',
  1: '出库中',
  2: '已出库',
};

type ListRow = { type: 'shipment'; key: string; s: ShipmentDTO };

const ShipmentList: React.FC = () => {
  const [rows, setRows] = useState<ListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [clientCode, setClientCode] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();
  const [weightModal, setWeightModal] = useState<ShipmentDTO | null>(null);
  const [submitDepotModal, setSubmitDepotModal] = useState<ShipmentDTO | null>(null);
  const [submitDepotContext, setSubmitDepotContext] = useState<SubmitDepotContextDTO | null>(null);
  const [submitDepotContextLoading, setSubmitDepotContextLoading] = useState(false);
  const [submitDepotForm] = Form.useForm<{ depotId: string; huolalaOrderNo?: string; photoUrls?: string }>();
  const [submitDepotSubmitting, setSubmitDepotSubmitting] = useState(false);
  const [submitInvoiceModal, setSubmitInvoiceModal] = useState<ShipmentDTO | null>(null);
  const [syncTrackingModal, setSyncTrackingModal] = useState<ShipmentDTO | null>(null);
  const [syncAgentDataModal, setSyncAgentDataModal] = useState<ShipmentDTO | null>(null);
  const [syncTrackingDataModal, setSyncTrackingDataModal] = useState<ShipmentDTO | null>(null);
  const [syncAgentDataSubmitting, setSyncAgentDataSubmitting] = useState(false);
  const [syncTrackingDataSubmitting, setSyncTrackingDataSubmitting] = useState(false);
  const [syncAgentDataFromDrawerLoading, setSyncAgentDataFromDrawerLoading] = useState(false);
  const [syncTrackingDataFromDrawerLoading, setSyncTrackingDataFromDrawerLoading] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [bindModalOpen, setBindModalOpen] = useState(false);
  const [bindingShipment, setBindingShipment] = useState<ShipmentDTO | null>(null);
  const [cartonsForBind, setCartonsForBind] = useState<CartonDTO[]>([]);
  const [selectedCartonIds, setSelectedCartonIds] = useState<string[]>([]);
  const [bindCartonLoading, setBindCartonLoading] = useState(false);
  /** 运单详情滑窗 */
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [detailShipment, setDetailShipment] = useState<ShipmentDTO | null>(null);
  const [detailShipmentLoading, setDetailShipmentLoading] = useState(false);
  const [detailCartons, setDetailCartons] = useState<CartonDTO[]>([]);
  const [detailInvoices, setDetailInvoices] = useState<ShipmentInvoiceDTO[]>([]);
  const [detailTrackingDisplay, setDetailTrackingDisplay] = useState<TrackingDisplayDTO | null>(null);
  const [detailTrackingDisplayLoading, setDetailTrackingDisplayLoading] = useState(false);
  const [cartonDetailDrawerOpen, setCartonDetailDrawerOpen] = useState(false);
  const [cartonDetailId, setCartonDetailId] = useState<string | undefined>(undefined);
  const [routes, setRoutes] = useState<RouteDTO[]>([]);
  const [customers, setCustomers] = useState<CustomerDTO[]>([]);
  const [addresses, setAddresses] = useState<{ id?: string; contactName?: string; phone?: string; address?: string }[]>([]);
  const [weightForm] = Form.useForm();
  const [createForm] = Form.useForm();

  const load = () => {
    setLoading(true);
    shipmentApi
      .list({ page, size: 20, clientCode, status: status || undefined })
      .then((res) => {
        const body = (res.data as any)?.data ?? res.data;
        const content = body?.content ?? [];
        const totalElements = body?.totalElements ?? 0;
        const shipRows: ListRow[] = (content as ShipmentDTO[]).map((s) => ({ type: 'shipment', key: `s-${s.shipmentId}`, s }));
        setRows(shipRows);
        setTotal(totalElements);
      })
      .catch(() => {
        setRows([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [page, clientCode, status]);

  useEffect(() => {
    routeApi.list(true).then((res) => setRoutes(res.data || [])).catch(() => setRoutes([]));
    customerApi.list().then((res) => setCustomers(res.data || [])).catch(() => setCustomers([]));
  }, []);

  const onCustomerChange = (customerCode: string) => {
    createForm.setFieldValue('clientAddr', undefined);
    if (!customerCode) {
      setAddresses([]);
      return;
    }
    customerApi
      .getByCode(customerCode)
      .then((res) => setAddresses(res.data?.addresses || []))
      .catch(() => setAddresses([]));
  };

  const onFinishCreate = (values: Record<string, unknown>) => {
    const selectedAddr = addresses.find((a) => a.id === values.clientAddr);
    const clientAddrStr = selectedAddr
      ? [selectedAddr.address, selectedAddr.contactName, selectedAddr.phone].filter(Boolean).join(' ')
      : ((values.clientAddr as string) || undefined);
    preDeclarationApi
      .create({
        clientCode: values.clientCode as string,
        clientAddr: clientAddrStr,
        routeCode: values.routeCode as string,
        expectedShipDate: values.expectedShipDate ? dayjs(values.expectedShipDate as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
        cargoDescJson: (values.cargoDescJson as string)?.trim() || '',
        customsDeclJson: undefined,
      })
      .then(() => {
        message.success('预申报创建成功');
        createForm.resetFields();
        setCreateModalOpen(false);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '创建失败'));
  };

  const openBindModal = (s: ShipmentDTO) => {
    setBindingShipment(s);
    setSelectedCartonIds([]);
    setBindModalOpen(true);
    cartonApi.list().then((res) => {
      const list = res.data || [];
      const clientCode = s.clientCode;
      const customer = customers.find((c) => c.customerCode === clientCode);
      const sameIds = [clientCode, customer?.id].filter(Boolean) as string[];
      const filtered = list.filter(
        (c) => c.belongClientId && sameIds.includes(c.belongClientId) && (!c.shipmentId || c.shipmentId === s.shipmentId)
      );
      setCartonsForBind(filtered);
    }).catch(() => setCartonsForBind([]));
  };

  const onBindCartons = () => {
    if (!bindingShipment || selectedCartonIds.length === 0) {
      message.warning('请勾选要绑定的货物');
      return;
    }
    setBindCartonLoading(true);
    const shipmentId = bindingShipment.shipmentId;
    Promise.all(selectedCartonIds.map((id) => cartonApi.update(id, { shipmentId })))
      .then(() => {
        message.success('绑定成功');
        setSelectedCartonIds([]);
        setBindModalOpen(false);
        setBindingShipment(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === shipmentId) {
          cartonApi.list().then((res) => {
            setDetailCartons((res.data || []).filter((c) => c.shipmentId === shipmentId));
          });
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '绑定失败'))
      .finally(() => setBindCartonLoading(false));
  };

  const openDetailDrawer = (s: ShipmentDTO) => {
    setDetailShipment(null);
    setDetailCartons([]);
    setDetailInvoices([]);
    setDetailTrackingDisplay(null);
    setDetailDrawerOpen(true);
    setDetailShipmentLoading(true);
    Promise.all([shipmentApi.get(s.shipmentId), cartonApi.list(), shipmentApi.listInvoices(s.shipmentId)])
      .then(([shipRes, cartonRes, invRes]) => {
        setDetailShipment(shipRes.data);
        setDetailCartons((cartonRes.data || []).filter((c) => c.shipmentId === s.shipmentId));
        setDetailInvoices(Array.isArray(invRes.data) ? invRes.data : []);
      })
      .catch(() => message.warning('加载运单详情失败'))
      .finally(() => setDetailShipmentLoading(false));
  };

  /** 当详情抽屉打开且运单有代理入库单号时，拉取轨迹展示数据 */
  useEffect(() => {
    if (!detailDrawerOpen || !detailShipment?.shipmentId || !detailShipment?.agentInboundNo?.trim()) {
      if (!detailDrawerOpen) setDetailTrackingDisplay(null);
      setDetailTrackingDisplayLoading(false);
      return;
    }
    setDetailTrackingDisplayLoading(true);
    shipmentApi
      .getTrackingDisplay(detailShipment.shipmentId)
      .then((res) => setDetailTrackingDisplay(res.data ?? null))
      .catch(() => setDetailTrackingDisplay(null))
      .finally(() => setDetailTrackingDisplayLoading(false));
  }, [detailDrawerOpen, detailShipment?.shipmentId, detailShipment?.agentInboundNo]);

  const refreshDetailInvoices = () => {
    if (!detailShipment?.shipmentId) return;
    shipmentApi.listInvoices(detailShipment.shipmentId).then((res) => {
      setDetailInvoices(Array.isArray(res.data) ? res.data : []);
    });
  };

  const refreshDetailCartons = () => {
    if (!detailShipment?.shipmentId) return;
    cartonApi.list().then((res) => {
      setDetailCartons((res.data || []).filter((c) => c.shipmentId === detailShipment.shipmentId));
    });
  };

  const onWeightOk = () => {
    const s = weightModal!;
    weightForm.validateFields().then((values) => {
      shipmentApi
        .weightConfirm({ shipmentId: s.shipmentId, confirmedWeight: values.confirmedWeight, accept: true })
        .then(() => {
          message.success('重量已确认');
          setWeightModal(null);
          weightForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'));
    });
  };

  const openSubmitDepotModal = (s: ShipmentDTO) => {
    setSubmitDepotModal(s);
    setSubmitDepotContext(null);
    submitDepotForm.resetFields();
    setSubmitDepotContextLoading(true);
    shipmentApi
      .getSubmitDepotContext(s.shipmentId)
      .then((res) => {
        setSubmitDepotContext(res.data);
      })
      .catch((e) => {
        message.error(e.response?.data?.message || '获取代理信息失败');
        setSubmitDepotModal(null);
      })
      .finally(() => setSubmitDepotContextLoading(false));
  };

  const onSubmitDepotOk = () => {
    const s = submitDepotModal!;
    submitDepotForm.validateFields().then((values) => {
      const body: SubmitToDepotRequest = {
        depotId: values.depotId,
        huolalaOrderNo: values.huolalaOrderNo?.trim() || undefined,
        photoUrls: values.photoUrls ? values.photoUrls.trim().split(/\s+/).filter(Boolean) : undefined,
      };
      setSubmitDepotSubmitting(true);
      shipmentApi
        .submitToAgentDepot(s.shipmentId, body)
        .then(() => {
          message.success('已提交代理仓');
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          submitDepotForm.resetFields();
          load();
        })
        .catch((e) => message.error(e.response?.data?.message || '操作失败'))
        .finally(() => setSubmitDepotSubmitting(false));
    });
  };

  const onGenerateInvoiceOk = () => {
    const s = submitInvoiceModal!;
    shipmentApi
      .generateInvoice(s.shipmentId)
      .then((res) => {
        message.success('发票已生成：' + (res.data?.fileName || ''));
        setSubmitInvoiceModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          refreshDetailInvoices();
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '生成发票失败'));
  };

  const onDownloadInvoice = (shipmentId: string, inv: ShipmentInvoiceDTO) => {
    shipmentApi
      .downloadInvoice(shipmentId, inv.id)
      .then((res) => {
        const blob = res.data as Blob;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = inv.fileName || 'invoice.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => message.error(e.response?.data?.message || '下载失败'));
  };

  const onSyncTrackingOk = () => {
    const s = syncTrackingModal!;
    shipmentApi
      .syncTracking(s.shipmentId)
      .then(() => {
        message.success('已同步轨迹，状态已更新');
        setSyncTrackingModal(null);
        load();
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'));
  };

  const onSyncAgentDataOk = () => {
    const s = syncAgentDataModal!;
    setSyncAgentDataSubmitting(true);
    shipmentApi
      .syncAgentData(s.shipmentId)
      .then(() => {
        message.success('已同步代理入仓数据');
        setSyncAgentDataModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          shipmentApi.get(s.shipmentId).then((res) => setDetailShipment(res.data));
          refreshDetailCartons();
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncAgentDataSubmitting(false));
  };

  const onSyncTrackingDataOk = () => {
    const s = syncTrackingDataModal!;
    setSyncTrackingDataSubmitting(true);
    shipmentApi
      .syncTrackingData(s.shipmentId)
      .then(() => {
        message.success('已同步轨迹数据');
        setSyncTrackingDataModal(null);
        load();
        if (detailDrawerOpen && detailShipment?.shipmentId === s.shipmentId) {
          shipmentApi.get(s.shipmentId).then((res) => setDetailShipment(res.data));
          shipmentApi.getTrackingDisplay(s.shipmentId).then((res) => setDetailTrackingDisplay(res.data ?? null)).catch(() => setDetailTrackingDisplay(null));
        }
      })
      .catch((e) => message.error(e.response?.data?.message || '操作失败'))
      .finally(() => setSyncTrackingDataSubmitting(false));
  };

  /** 在详情抽屉内点击「同步轨迹数据」：同步成功后刷新当前详情 */
  const syncTrackingDataFromDrawer = () => {
    if (!detailShipment?.shipmentId || !detailShipment.agentInboundNo?.trim()) {
      message.warning('当前运单无代理入库单号，请先执行「交代理仓」');
      return;
    }
    if (detailShipment.status === 'DELIVERED') {
      message.warning('运单已签收，无需同步轨迹');
      return;
    }
    setSyncTrackingDataFromDrawerLoading(true);
    shipmentApi
      .syncTrackingData(detailShipment.shipmentId)
      .then(() => {
        message.success('已同步轨迹数据');
        return Promise.all([
          shipmentApi.get(detailShipment!.shipmentId),
          shipmentApi.getTrackingDisplay(detailShipment!.shipmentId),
        ]);
      })
      .then(([shipRes, trackRes]) => {
        setDetailShipment(shipRes.data);
        setDetailTrackingDisplay(trackRes.data ?? null);
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncTrackingDataFromDrawerLoading(false));
  };

  /** 在详情抽屉内点击「同步代理入仓数据」：同步成功后刷新当前详情 */
  const syncAgentDataFromDrawer = () => {
    if (!detailShipment?.shipmentId || !detailShipment.agentInboundNo?.trim()) {
      message.warning('当前运单无代理入库单号，请先执行「交代理仓」');
      return;
    }
    setSyncAgentDataFromDrawerLoading(true);
    shipmentApi
      .syncAgentData(detailShipment.shipmentId)
      .then(() => {
        message.success('已同步代理入仓数据');
        return shipmentApi.get(detailShipment!.shipmentId);
      })
      .then((res) => {
        setDetailShipment(res.data);
        refreshDetailCartons();
      })
      .catch((e) => message.error(e.response?.data?.message || '同步失败'))
      .finally(() => setSyncAgentDataFromDrawerLoading(false));
  };

  const getShipmentStatus = (s: ShipmentDTO) => String(s?.status ?? '').trim().toUpperCase();

  const getStatusDisplay = (row: ListRow): string => {
    const st = getShipmentStatus(row.s);
    return statusLabel[st] || row.s.status || st;
  };

  /** 所有操作按钮均展示，样式与「绑定货物」一致（link + 无 padding），状态不允许的禁用并通过 Tooltip 提示 */
  const renderActionButton = (
    key: string,
    label: string,
    allowed: boolean,
    tooltipWhenDisabled: string,
    onClick: () => void
  ) => {
    const btn = (
      <Button
        key={key}
        type="link"
        size="small"
        style={{ padding: 0 }}
        disabled={!allowed}
        onClick={allowed ? onClick : undefined}
      >
        {label}
      </Button>
    );
    return allowed ? btn : <Tooltip key={key} title={tooltipWhenDisabled}>{span(btn)}</Tooltip>;
  };
  const span = (node: React.ReactNode) => <span style={{ display: 'inline-block' }}>{node}</span>;

  const renderActions = (row: ListRow) => {
    const s = row.s;
    const st = getShipmentStatus(s);
    if (st === 'DELIVERED') return <span style={{ color: '#999' }}>已签收，不可修改</span>;

    const canBind = st === 'PRE_DECLARATION';
    const canSubmitDepot = st === 'PRE_DECLARATION';
    const canWeight = st === 'SUBMITTED_TO_DEPOT' || st === 'HANDOVER_CONFIRMING';
    const canSubmitInvoice = st === 'SUBMITTED_TO_DEPOT';
    const canSyncTracking = ['CONFIRMED_WAIT_DEPARTURE', 'IN_TRANSIT', 'OUT_FOR_DELIVERY'].includes(st);
    const canSyncAgentData = !!(s.agentInboundNo && s.agentInboundNo.trim());
    const canSyncTrackingData = !!(s.agentInboundNo && s.agentInboundNo.trim()) && st !== 'DELIVERED';

    return (
      <Space size="small" wrap>
        {renderActionButton(
          'bind',
          '绑定货物',
          canBind,
          '当前状态不允许执行此操作，需为预申报',
          () => openBindModal(s)
        )}
        {renderActionButton(
          'submit-depot',
          '交代理仓',
          canSubmitDepot,
          '当前状态不允许执行此操作，需为预申报',
          () => openSubmitDepotModal(s)
        )}
        {renderActionButton(
          'weight',
          '确认重量',
          canWeight,
          '当前状态不允许执行此操作，需为已交代理仓或交货确认中',
          () => setWeightModal(s)
        )}
        {renderActionButton(
          'generate-invoice',
          '生成发票',
          canSubmitInvoice,
          '当前状态不允许执行此操作，需为已交代理仓',
          () => setSubmitInvoiceModal(s)
        )}
        {renderActionButton(
          'sync-agent',
          '同步代理入仓数据',
          canSyncAgentData,
          '请先执行「交代理仓」并获取代理入库单号后再同步',
          () => setSyncAgentDataModal(s)
        )}
        {renderActionButton(
          'sync-tracking-data',
          '同步轨迹数据',
          canSyncTrackingData,
          '请先执行「交代理仓」并获取代理入库单号；已签收运单不可同步',
          () => setSyncTrackingDataModal(s)
        )}
        {renderActionButton(
          'sync',
          '同步轨迹',
          canSyncTracking,
          '当前状态不允许执行此操作，需为准备运输、运输中或待派送',
          () => setSyncTrackingModal(s)
        )}
      </Space>
    );
  };

  return (
    <>
      <Card
        title="运单列表"
        style={{ marginBottom: 16 }}
        extra={
          <Button type="primary" onClick={() => setCreateModalOpen(true)}>
            创建预申报
          </Button>
        }
      >
        <Input.Search
          placeholder="客户编号"
          allowClear
          style={{ width: 200, marginRight: 8 }}
          onSearch={setClientCode}
        />
        <Select
          placeholder="状态"
          allowClear
          style={{ width: 140, marginRight: 8 }}
          options={statusOptions}
          onChange={setStatus}
        />
        <Button onClick={load}>查询</Button>
      </Card>
      <Card>
        <Table
          loading={loading}
          dataSource={rows}
          rowKey="key"
          onRow={(record) => ({
            onClick: () => openDetailDrawer(record.s),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: '运单号/预申报单号',
              width: 160,
              render: (_, r: ListRow) => r.s.shipmentId,
            },
            {
              title: '客户',
              width: 100,
              render: (_, r: ListRow) => r.s.clientCode,
            },
            {
              title: '线路',
              width: 120,
              render: (_, r: ListRow) => r.s.routeName || '-',
            },
            { title: '状态', width: 100, render: (_, r: ListRow) => getStatusDisplay(r) },
            {
              title: '代理入库单号',
              width: 120,
              render: (_, r: ListRow) => r.s.agentInboundNo || '-',
            },
            {
              title: '转单号',
              width: 140,
              render: (_, r: ListRow) => r.s.channelShipmentId || '-',
            },
            {
              title: '计费重',
              width: 80,
              render: (_, r: ListRow) => (r.s.confirmedWeight != null ? r.s.confirmedWeight : '-'),
            },
            { title: '操作', width: 260, render: (_, r: ListRow) => <span onClick={(e) => e.stopPropagation()}>{renderActions(r)}</span> },
          ]}
          pagination={{
            current: page + 1,
            total,
            pageSize: 20,
            onChange: (p) => setPage(p - 1),
          }}
        />
      </Card>

      <Modal
        title="创建预申报"
        open={createModalOpen}
        onCancel={() => {
          setCreateModalOpen(false);
          createForm.resetFields();
        }}
        footer={null}
        width={560}
      >
        <Form form={createForm} layout="vertical" onFinish={onFinishCreate}>
          <Form.Item name="clientCode" label="客户编号" rules={[{ required: true, message: '请选择客户' }]}>
            <Select
              placeholder="从客户列表选择"
              showSearch
              optionFilterProp="label"
              onChange={onCustomerChange}
              options={customers.map((c) => ({ label: `${c.customerCode} - ${c.name || ''}`, value: c.customerCode }))}
            />
          </Form.Item>
          <Form.Item name="clientAddr" label="客户地址" rules={[{ required: true, message: '请选择地址' }]}>
            <Select
              placeholder={addresses.length ? '选择地址' : '请先选择客户'}
              options={addresses.map((a) => ({
                label: [a.contactName, a.address, a.phone].filter(Boolean).join(' | ') || a.id,
                value: a.id,
              }))}
              disabled={!addresses.length}
            />
          </Form.Item>
          <Form.Item name="routeCode" label="线路" rules={[{ required: true, message: '请选择线路' }]}>
            <Select placeholder="选择线路" options={routes.map((r) => ({ label: r.routeCode, value: r.routeCode }))} />
          </Form.Item>
          <Form.Item name="expectedShipDate" label="预计发货日">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cargoDescJson" label="货物描述">
            <Input.TextArea rows={3} placeholder="请输入货物描述（文本）" />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit">
              确定创建
            </Button>
            <Button style={{ marginLeft: 8 }} onClick={() => setCreateModalOpen(false)}>
              取消
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="绑定货物"
        open={bindModalOpen}
        onCancel={() => {
          setBindModalOpen(false);
          setBindingShipment(null);
        }}
        footer={null}
        width={720}
      >
        {bindingShipment && (
          <div style={{ marginBottom: 16 }}>
            <span style={{ marginRight: 8 }}>当前运单：</span>
            <strong>{bindingShipment.shipmentId}</strong>
            <span style={{ marginLeft: 8, color: '#666' }}>（客户 {bindingShipment.clientCode}）</span>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <Button type="primary" onClick={onBindCartons} loading={bindCartonLoading} disabled={selectedCartonIds.length === 0}>
            绑定选中
          </Button>
        </div>
        <Table
          rowSelection={{
            selectedRowKeys: selectedCartonIds,
            onChange: (keys) => setSelectedCartonIds(keys as string[]),
          }}
          dataSource={cartonsForBind}
          rowKey="id"
          size="small"
          columns={[
            { title: '货物标识', dataIndex: 'cartonId', width: 160, ellipsis: true },
            { title: '客户', dataIndex: 'belongClientId', width: 120, render: (v: string) => customers.find((c) => c.id === v || c.customerCode === v)?.name || v || '-' },
            { title: '状态', dataIndex: 'state', width: 100, render: (v: string) => cartonStateLabel[v || ''] || v || '-' },
            { title: '创建时间', dataIndex: 'createTimestamp', width: 160, render: (t: number) => (t ? new Date(t).toLocaleString() : '-') },
          ]}
          pagination={{ pageSize: 5 }}
        />
      </Modal>

      <Modal
        title="确认重量"
        open={!!weightModal}
        onOk={onWeightOk}
        onCancel={() => {
          setWeightModal(null);
          weightForm.resetFields();
        }}
      >
        {weightModal && (
          <Form form={weightForm} layout="vertical">
            <Form.Item
              name="confirmedWeight"
              label="确认计费重(kg)"
              rules={[{ required: true }]}
              initialValue={weightModal.pendingWeight}
            >
              <InputNumber min={0} step={0.1} style={{ width: '100%' }} />
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="交代理仓"
        open={!!submitDepotModal}
        onOk={onSubmitDepotOk}
        onCancel={() => {
          setSubmitDepotModal(null);
          setSubmitDepotContext(null);
          submitDepotForm.resetFields();
        }}
        confirmLoading={submitDepotSubmitting}
        width={560}
        destroyOnClose
      >
        {submitDepotModal && (
          <>
            <p style={{ marginBottom: 12 }}>
              运单 <strong>{submitDepotModal.shipmentId}</strong> 将提交至代理仓，请选择目标仓库并填写交仓信息。
            </p>
            {submitDepotContextLoading ? (
              <Spin spinning />
            ) : submitDepotContext ? (
              <>
                {submitDepotContext.agent && (
                  <div style={{ marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 8 }}>
                    <div style={{ marginBottom: 4 }}>
                      <strong>代理：</strong>
                      {submitDepotContext.agent.basicInfo?.name || submitDepotContext.agent.basicInfo?.identifier || '-'}
                    </div>
                    {submitDepotContext.agent.contact?.contactPerson && (
                      <div style={{ color: '#666', fontSize: 12 }}>
                        对接人：{submitDepotContext.agent.contact.contactPerson.name} {submitDepotContext.agent.contact.contactPerson.phone}
                      </div>
                    )}
                  </div>
                )}
                <Form form={submitDepotForm} layout="vertical">
                  <Form.Item
                    name="depotId"
                    label="代理仓库"
                    rules={[{ required: true, message: '请选择目标仓库' }]}
                  >
                    <Select
                      placeholder="选择仓库"
                      options={(submitDepotContext.depots || []).map((d) => ({
                        label: d.depotName ? `${d.depotName}${d.depotId ? ` (${d.depotId})` : ''}` : String(d.depotId ?? d.id ?? ''),
                        value: String(d.depotId ?? d.id ?? ''),
                      }))}
                      allowClear={false}
                    />
                  </Form.Item>
                  <Form.Item name="huolalaOrderNo" label="货拉拉单号">
                    <Input placeholder="选填" />
                  </Form.Item>
                  <Form.Item name="photoUrls" label="照片（多张 URL 空格分隔）">
                    <Input.TextArea rows={2} placeholder="选填，多张照片 URL 用空格分隔" />
                  </Form.Item>
                </Form>
              </>
            ) : null}
          </>
        )}
      </Modal>

      <Modal
        title="生成发票"
        open={!!submitInvoiceModal}
        onOk={onGenerateInvoiceOk}
        onCancel={() => setSubmitInvoiceModal(null)}
      >
        {submitInvoiceModal && (
          <p>确定要为运单 <strong>{submitInvoiceModal.shipmentId}</strong> 生成发票 Excel？将根据运单、路线、客户、地址及产品信息按模板生成文件。</p>
        )}
      </Modal>

      <Modal
        title="同步轨迹"
        open={!!syncTrackingModal}
        onOk={onSyncTrackingOk}
        onCancel={() => setSyncTrackingModal(null)}
      >
        {syncTrackingModal && (
          <p>
            确定对运单 {syncTrackingModal.shipmentId} 执行同步轨迹？
            {syncTrackingModal.status === 'CONFIRMED_WAIT_DEPARTURE' && ' 状态将更新为「运输中」。'}
            {syncTrackingModal.status === 'IN_TRANSIT' && ' 状态将更新为「待派送」。'}
            {syncTrackingModal.status === 'OUT_FOR_DELIVERY' && ' 状态将更新为「已签收」。'}
          </p>
        )}
      </Modal>

      <Modal
        title="同步代理入仓数据"
        open={!!syncAgentDataModal}
        onOk={onSyncAgentDataOk}
        onCancel={() => setSyncAgentDataModal(null)}
        confirmLoading={syncAgentDataSubmitting}
      >
        {syncAgentDataModal && (
          <p>
            确定对运单 <strong>{syncAgentDataModal.shipmentId}</strong> 执行同步代理入仓数据？将从代理系统拉取入库状态、出库状态、测量数据，并更新运单及该运单下货物状态（不含轨迹）。
          </p>
        )}
      </Modal>

      <Modal
        title="同步轨迹数据"
        open={!!syncTrackingDataModal}
        onOk={onSyncTrackingDataOk}
        onCancel={() => setSyncTrackingDataModal(null)}
        confirmLoading={syncTrackingDataSubmitting}
      >
        {syncTrackingDataModal && (
          <p>
            确定对运单 <strong>{syncTrackingDataModal.shipmentId}</strong> 执行同步轨迹数据？将从代理系统拉取运单轨迹并更新展示。
          </p>
        )}
      </Modal>

      <Drawer
        title="运单详情"
        placement="right"
        width="56%"
        open={detailDrawerOpen}
        onClose={() => setDetailDrawerOpen(false)}
        closable
        destroyOnClose
      >
        {detailShipmentLoading ? (
          <Spin spinning />
        ) : detailShipment ? (
          <>
            <Card size="small" title={<span style={{ fontWeight: 600, fontSize: 15 }}>基础信息</span>} style={{ marginBottom: 16 }}>
              <Descriptions column={1} size="small">
                <Descriptions.Item label="运单号">{detailShipment.shipmentId}</Descriptions.Item>
                <Descriptions.Item label="客户">{detailShipment.clientCode}</Descriptions.Item>
                <Descriptions.Item label="线路">{detailShipment.routeName || '-'}</Descriptions.Item>
                <Descriptions.Item label="状态">{statusLabel[getShipmentStatus(detailShipment)] || detailShipment.status}</Descriptions.Item>
                <Descriptions.Item label="代理入库单号">{detailShipment.agentInboundNo || '-'}</Descriptions.Item>
                <Descriptions.Item label="转单号">{detailShipment.channelShipmentId || '-'}</Descriptions.Item>
                <Descriptions.Item label="计费重">{detailShipment.confirmedWeight != null ? detailShipment.confirmedWeight : '-'}</Descriptions.Item>
                <Descriptions.Item label="客户地址">{detailShipment.clientAddr || '-'}</Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>入仓数据</span>}
              extra={
                <Button
                  type="primary"
                  size="small"
                  loading={syncAgentDataFromDrawerLoading}
                  disabled={!detailShipment.agentInboundNo?.trim()}
                  onClick={syncAgentDataFromDrawer}
                >
                  同步代理入仓数据
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="代理入库状态">
                  {detailShipment.agentInboundStatus != null
                    ? agentInboundStatusLabel[detailShipment.agentInboundStatus] ?? String(detailShipment.agentInboundStatus)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理出库状态">
                  {detailShipment.agentOutboundStatus != null
                    ? agentOutboundStatusLabel[detailShipment.agentOutboundStatus] ?? String(detailShipment.agentOutboundStatus)
                    : '-'}
                </Descriptions.Item>
                <Descriptions.Item label="代理测量数据">
                  {detailShipment.agentMeasurementDataJson ? (
                    <pre style={{ margin: 0, maxHeight: 160, overflow: 'auto', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                      {(() => {
                        try {
                          return JSON.stringify(JSON.parse(detailShipment.agentMeasurementDataJson), null, 2);
                        } catch {
                          return detailShipment.agentMeasurementDataJson;
                        }
                      })()}
                    </pre>
                  ) : (
                    '-'
                  )}
                </Descriptions.Item>
              </Descriptions>
            </Card>

            <Card
              size="small"
              title={<span style={{ fontWeight: 600, fontSize: 15 }}>轨迹数据</span>}
              extra={
                <Button
                  type="primary"
                  size="small"
                  loading={syncTrackingDataFromDrawerLoading}
                  disabled={!detailShipment.agentInboundNo?.trim() || detailShipment.status === 'DELIVERED'}
                  onClick={syncTrackingDataFromDrawer}
                >
                  同步轨迹数据
                </Button>
              }
              style={{ marginBottom: 16 }}
            >
              <TrackingDisplayPanel
                data={detailTrackingDisplay}
                fallbackEvents={detailShipment.agentTrackingLatest ?? undefined}
                loading={detailTrackingDisplayLoading}
              />
            </Card>

            <Card size="small" title={<span style={{ fontWeight: 600, fontSize: 15 }}>发票</span>} style={{ marginBottom: 16 }}>
              {detailInvoices.length === 0 ? (
                <div style={{ color: '#999' }}>暂无发票记录，可在列表操作中点击「生成发票」</div>
              ) : (
                <Table
                  dataSource={detailInvoices}
                  rowKey="id"
                  size="small"
                  pagination={false}
                  columns={[
                    { title: '文件名', dataIndex: 'fileName', ellipsis: true, render: (v: string) => v || '-' },
                    { title: '生成时间', dataIndex: 'generatedAt', width: 180, render: (v: string) => (v ? new Date(v).toLocaleString() : '-') },
                    {
                      title: '操作',
                      width: 80,
                      render: (_: unknown, inv: ShipmentInvoiceDTO) => (
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (detailShipment?.shipmentId) onDownloadInvoice(detailShipment.shipmentId, inv);
                          }}
                        >
                          下载
                        </Button>
                      ),
                    },
                  ]}
                />
              )}
            </Card>

            <Card size="small" title={<span style={{ fontWeight: 600, fontSize: 15 }}>绑定的货物</span>} style={{ marginBottom: 16 }}>
              <Table
                dataSource={detailCartons}
                rowKey="id"
                size="small"
                pagination={false}
                onRow={(record) => ({
                  onClick: () => {
                    if (record.id) {
                      setCartonDetailId(record.id);
                      setCartonDetailDrawerOpen(true);
                    }
                  },
                  style: { cursor: 'pointer' },
                })}
                columns={[
                  { title: '货物标识', dataIndex: 'cartonId', width: 160, ellipsis: true },
                  { title: '客户', dataIndex: 'belongClientId', width: 120, render: (v: string) => customers.find((c) => c.id === v || c.customerCode === v)?.name || v || '-' },
                  { title: '状态', dataIndex: 'state', width: 100, render: (v: string) => cartonStateLabel[v || ''] || v || '-' },
                ]}
              />
            </Card>
          </>
        ) : null}
      </Drawer>

      <CartonDetailDrawer
        open={cartonDetailDrawerOpen}
        cartonId={cartonDetailId}
        onClose={() => {
          setCartonDetailDrawerOpen(false);
          setCartonDetailId(undefined);
        }}
        onSaved={refreshDetailCartons}
      />
    </>
  );
};

export default ShipmentList;
